/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useCallback, useEffect, useRef } from "react";

import { useDeviceType } from "../../../shared/hooks/useDeviceType";
import { usePlayerFullScreen } from "../../Controller";

const apiSupported =
  screen.orientation &&
  // @ts-ignore
  typeof screen.orientation.lock === "function" &&
  typeof screen.orientation.unlock === "function";

export const useScreenOrientationLocker = (enabled?: boolean) => {
  const lockedRef = useRef<boolean>(false);

  const { isMobilePhoneDevice } = useDeviceType();

  const {
    isFullScreen,
    toggle: toggleFullScreen,
    reset: resetFullScreen,
  } = usePlayerFullScreen();

  const lock = useCallback(
    () =>
      screen.orientation
        // @ts-ignore
        .lock("landscape")
        .then(() => {
          lockedRef.current = true;
        })
        .catch(() => {
          console.debug("Orientation lock is not supported on this device");
          resetFullScreen().catch(() =>
            console.debug("Unable to reset full screen"),
          );
        }),
    [resetFullScreen],
  );

  useEffect(() => {
    if (!enabled || !apiSupported || !isMobilePhoneDevice) {
      return;
    }

    // full screen is required before locking orientation
    !isFullScreen ? toggleFullScreen().then(lock) : lock();

    return () => {
      if (lockedRef.current) {
        screen.orientation.unlock();
        lockedRef.current = false;
      }
    };
  }, [enabled, isFullScreen, isMobilePhoneDevice, lock, toggleFullScreen]);
};
